import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppState } from './AppState.js';
import { EmptyState, ErrorPanel, LoadingPanel, PageIntro, PanelHeader } from './components.js';
import { mapError } from './errors.js';
import { useInvalidateQueries, useRequiredLeagueId, useResolvedActiveSeason, useSyncLeagueFromRoute } from './hooks.js';
import { toDateInputValue } from '../lib/formatting.js';

function badge(status: string) {
  return <span className={`badge ${status || 'unknown'}`}>{status || 'unknown'}</span>;
}

function hammerOptions() {
  return Array.from({ length: 9 }, (_value, index) => index + 1);
}

function summarizePairingDraft(matches: Array<{ playerAId: string; playerBId: string }>) {
  const duplicatePlayerIds = new Set<string>();
  const seen = new Set<string>();
  let incompletePairings = 0;

  for (const match of matches) {
    if (!match.playerAId || !match.playerBId) {
      incompletePairings += 1;
    }

    for (const playerId of [match.playerAId, match.playerBId]) {
      if (!playerId) {
        continue;
      }
      if (seen.has(playerId)) {
        duplicatePlayerIds.add(playerId);
      }
      seen.add(playerId);
    }
  }

  return {
    duplicatePlayerIds: [...duplicatePlayerIds],
    incompletePairings,
  };
}

export function AdminMatchDaysPage() {
  const leagueId = useRequiredLeagueId();
  useSyncLeagueFromRoute(leagueId);
  const { client, setBanner } = useAppState();
  const { invalidate } = useInvalidateQueries();
  const { season } = useResolvedActiveSeason(leagueId);
  const matchDaysQuery = useQuery({
    queryKey: ['matchDays', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getMatchDays(leagueId, season!.id)).matchDays,
  });
  const seasonsQuery = useQuery({
    queryKey: ['seasons', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getSeasons(leagueId)).seasons,
  });
  const coursesQuery = useQuery({
    queryKey: ['courses', leagueId],
    enabled: Boolean(client),
    queryFn: async () => (await client!.getCourses(leagueId)).courses,
  });

  const [draft, setDraft] = useState<{
    date: string;
    seasonId: string;
    courseId: string | null;
    hammerHole: string;
    isPlayoffWeek: boolean;
    matches: Array<{ playerAId: string; playerBId: string }>;
  }>({
    date: '',
    seasonId: '',
    courseId: null,
    hammerHole: '',
    isPlayoffWeek: false,
    matches: [],
  });
  const seasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, draft.seasonId || null],
    enabled: Boolean(client && draft.seasonId),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, draft.seasonId)).seasonPlayers,
  });
  const activeSeasonPlayersQuery = useQuery({
    queryKey: ['seasonPlayers', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonPlayers(leagueId, season!.id)).seasonPlayers,
  });
  const attendanceQuery = useQuery({
    queryKey: ['attendance', leagueId, season?.id ?? null],
    enabled: Boolean(client && season),
    queryFn: async () => (await client!.getSeasonAttendance(leagueId, season!.id)).attendance,
  });

  useEffect(() => {
    if (!season?.id) {
      return;
    }
    setDraft((current) =>
      current.matches.length === 0 &&
      !current.date &&
      current.courseId === null &&
      !current.hammerHole &&
      current.seasonId !== season.id
        ? {
            ...current,
            seasonId: season.id,
          }
        : current);
  }, [season?.id]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const duplicatePlayerIds = new Set<string>();
      const seen = new Set<string>();
      for (const match of draft.matches) {
        if (!match.playerAId || !match.playerBId) {
          throw new Error('Finish both players for each match row or remove incomplete rows before saving.');
        }
        if (match.playerAId === match.playerBId) {
          throw new Error('Player A and Player B must be different.');
        }
        for (const playerId of [match.playerAId, match.playerBId]) {
          if (seen.has(playerId)) {
            duplicatePlayerIds.add(playerId);
          }
          seen.add(playerId);
        }
      }
      if (duplicatePlayerIds.size > 0) {
        throw new Error('A player cannot appear in multiple matches.');
      }
      return client!.createMatchDay(leagueId, {
        date: draft.date,
        seasonId: draft.seasonId,
        courseId: draft.courseId,
        hammerHole: draft.hammerHole ? Number(draft.hammerHole) : null,
        isPlayoffWeek: draft.isPlayoffWeek,
        matches: draft.matches,
      });
    },
    onSuccess: async () => {
      setBanner('success', 'Match day created.');
      setDraft({ date: '', seasonId: '', courseId: null, hammerHole: '', isPlayoffWeek: false, matches: [] });
      await invalidate(['matchDays', leagueId], ['matchDays', leagueId, draft.seasonId]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      matchDayId,
      updates,
    }: {
      matchDayId: string;
      updates: {
        date?: string;
        courseId?: string | null;
        hammerHole?: string;
        isPlayoffWeek?: boolean;
      };
    }) => {
      const payload: {
        date?: string;
        courseId?: string | null;
        hammerHole?: number | null;
        isPlayoffWeek?: boolean;
      } = {};
      if (updates.date !== undefined) {
        payload.date = updates.date;
      }
      if (updates.courseId !== undefined) {
        payload.courseId = updates.courseId;
      }
      if (updates.hammerHole !== undefined) {
        payload.hammerHole = updates.hammerHole ? Number(updates.hammerHole) : null;
      }
      if (updates.isPlayoffWeek !== undefined) {
        payload.isPlayoffWeek = updates.isPlayoffWeek;
      }
      return client!.updateMatchDay(leagueId, matchDayId, payload);
    },
    onSuccess: async () => {
      setBanner('success', 'Match day updated.');
      await invalidate(['matchDays', leagueId], ['matchDays', leagueId, season?.id ?? null]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (matchDayId: string) => client!.deleteMatchDay(leagueId, matchDayId),
    onSuccess: async () => {
      setBanner('success', 'Match day deleted.');
      await invalidate(['matchDays', leagueId], ['matchDays', leagueId, season?.id ?? null]);
    },
    onError: (error: unknown) => setBanner('error', mapError(error)),
  });

  if (seasonsQuery.isLoading || coursesQuery.isLoading || seasonPlayersQuery.isLoading || activeSeasonPlayersQuery.isLoading || attendanceQuery.isLoading || (season ? matchDaysQuery.isLoading : false)) {
    return <LoadingPanel title="Match Day Setup" message="Loading match-day setup." />;
  }

  if ((season && matchDaysQuery.isError) || seasonsQuery.isError || coursesQuery.isError || seasonPlayersQuery.isError || activeSeasonPlayersQuery.isError || attendanceQuery.isError || !seasonsQuery.data || !coursesQuery.data) {
    return <ErrorPanel title="Match Day Setup" message={mapError(matchDaysQuery.error ?? seasonsQuery.error ?? coursesQuery.error ?? seasonPlayersQuery.error ?? activeSeasonPlayersQuery.error ?? attendanceQuery.error)} />;
  }

  const matchDays = matchDaysQuery.data ?? [];
  const courseNameById = new Map(coursesQuery.data.map((course) => [course.id, course.name]));
  const selectedSeasonPlayers = seasonPlayersQuery.data ?? [];
  const pairingMembers = selectedSeasonPlayers.length > 0
    ? selectedSeasonPlayers.map((seasonPlayer) => ({
        player_id: seasonPlayer.player_id,
        ...(seasonPlayer.player !== undefined ? { player: seasonPlayer.player } : {}),
      }))
    : [];
  const draftSummary = summarizePairingDraft(draft.matches);
  const hasPairingConflicts = draftSummary.duplicatePlayerIds.length > 0 || draftSummary.incompletePairings > 0;

  return (
    <>
      <PageIntro
        title="Match Day Setup"
      />
      <section className="grid two">
        <article className="panel panel-accent">
          <PanelHeader title="Create Match Day" />
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              void createMutation.mutateAsync();
            }}
          >
            <div className="row">
              <div className="field">
                <label>Date</label>
                <input type="date" required value={draft.date} onChange={(event) => setDraft((current) => ({ ...current, date: event.target.value }))} />
              </div>
              <div className="field">
                <label>Season</label>
                <select
                  required
                  value={draft.seasonId}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      seasonId: event.target.value,
                      matches: [],
                    }))
                  }
                >
                  <option value="">Select season...</option>
                  {seasonsQuery.data.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="row">
              <div className="field">
                <label>Course</label>
                <select value={draft.courseId ?? ''} onChange={(event) => setDraft((current) => ({ ...current, courseId: event.target.value || null }))}>
                  <option value="">TBD</option>
                  {coursesQuery.data.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Hammer Hole</label>
                <select value={draft.hammerHole} onChange={(event) => setDraft((current) => ({ ...current, hammerHole: event.target.value }))}>
                  <option value="">No hammer hole</option>
                  {hammerOptions().map((hole) => (
                    <option key={hole} value={hole}>
                      Hole {hole}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="field">
              <label className="absent-toggle">
                <input
                  type="checkbox"
                  checked={draft.isPlayoffWeek}
                  onChange={(event) => setDraft((current) => ({ ...current, isPlayoffWeek: event.target.checked }))}
                />
                This is a playoff week
              </label>
              <p className="muted">
                Playoff weeks skip matchups. Any season player can log a standalone round for skins and handicap
                tracking, but standings and points are never affected.
              </p>
            </div>
            <div className="field">
              <label>Pairings</label>
              {!draft.seasonId ? (
                <div className="season-roster-note-block">Select a season first to load the eligible season roster for pairings.</div>
              ) : selectedSeasonPlayers.length === 0 ? (
                <EmptyState title="No season players available" message="Assign players to this season before creating match-day pairings." />
              ) : (
                <div className="stack">
                  {draftSummary.duplicatePlayerIds.length > 0 ? (
                    <div className="alert warn">One or more players are assigned more than once. Resolve duplicate pairings before saving.</div>
                  ) : null}
                  {draftSummary.incompletePairings > 0 ? (
                    <div className="alert warn">Finish both player slots for every pairing row before saving.</div>
                  ) : null}
                  <div className="inline-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          matches: (() => {
                            const selectedIds = new Set(
                              current.matches.flatMap((entry) => [entry.playerAId, entry.playerBId]).filter(Boolean),
                            );
                            const availablePlayerIds = selectedSeasonPlayers
                              .map((seasonPlayer) => seasonPlayer.player_id)
                              .filter((playerId) => !selectedIds.has(playerId));
                            const nextMatches = [...current.matches];
                            for (let index = 0; index + 1 < availablePlayerIds.length; index += 2) {
                              nextMatches.push({
                                playerAId: availablePlayerIds[index]!,
                                playerBId: availablePlayerIds[index + 1]!,
                              });
                            }
                            return nextMatches;
                          })(),
                        }))
                      }
                    >
                      Auto-Pair Remaining
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((current) => ({
                          ...current,
                          matches: [...current.matches, { playerAId: '', playerBId: '' }],
                        }))
                      }
                      >
                        Add Match
                      </button>
                    </div>
                  <div className="stack">
                    {draft.matches.length === 0 ? <p className="muted">No matches yet. Add a manual row or auto-pair the remaining season players.</p> : null}
                    {draft.matches.map((match, index) => (
                      <PairingRow
                        key={index}
                        match={match}
                        members={pairingMembers}
                        selected={draft.matches}
                        onChange={(next) =>
                          setDraft((current) => ({
                            ...current,
                            matches: current.matches.map((entry, entryIndex) => (entryIndex === index ? next : entry)),
                          }))
                        }
                        onRemove={() =>
                          setDraft((current) => ({
                            ...current,
                            matches: current.matches.filter((_entry, entryIndex) => entryIndex !== index),
                          }))
                        }
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="inline-actions">
              <button type="submit" disabled={createMutation.isPending || !draft.seasonId || hasPairingConflicts}>
                Create Match Day
              </button>
            </div>
          </form>
        </article>

        <article className="panel">
          <PanelHeader
            title={`${season?.name ?? 'Selected Season'} Match Days (${matchDays.length})`}
          />
          {matchDays.length === 0 ? (
            <EmptyState title="No match days yet" message={season ? 'Create the first match day to start scheduling rounds.' : 'Select an active season to view its match days.'} />
          ) : (
            <div className="card-list">
              {[...matchDays]
                .sort((left, right) => left.date.localeCompare(right.date))
                .map((matchDay) => (
                  <MatchDayCard
                    key={matchDay.id}
                    leagueId={leagueId}
                    matchDay={matchDay}
                    courses={coursesQuery.data}
                    courseName={matchDay.course_id ? courseNameById.get(matchDay.course_id) || matchDay.course_id : 'TBD'}
                    onUpdate={(updates) =>
                      updateMutation.mutateAsync({ matchDayId: matchDay.id, updates })
                    }
                    onDelete={() => void deleteMutation.mutateAsync(matchDay.id)}
                  />
                ))}
            </div>
          )}
        </article>
      </section>
    </>
  );
}

function PairingRow({
  match,
  members,
  selected,
  onChange,
  onRemove,
}: {
  match: { playerAId: string; playerBId: string };
  members: Array<{ player_id: string; player?: { name?: string } | null }>;
  selected: Array<{ playerAId: string; playerBId: string }>;
  onChange: (match: { playerAId: string; playerBId: string }) => void;
  onRemove: () => void;
}) {
  const selectedIds = useMemo(
    () => new Set(selected.flatMap((entry) => [entry.playerAId, entry.playerBId]).filter(Boolean)),
    [selected],
  );

  const optionsFor = (currentValue: string) =>
    members.filter((member) => !selectedIds.has(member.player_id) || member.player_id === currentValue);

  return (
    <div className="card">
      <div className="row" style={{ alignItems: 'end', gap: '1rem' }}>
        <div className="field" style={{ flex: 1 }}>
          <label>Player A</label>
          <select value={match.playerAId} onChange={(event) => onChange({ ...match, playerAId: event.target.value })}>
            <option value="">Select Player A</option>
            {optionsFor(match.playerAId).map((member) => (
              <option key={member.player_id} value={member.player_id}>
                {member.player?.name || member.player_id}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Player B</label>
          <select value={match.playerBId} onChange={(event) => onChange({ ...match, playerBId: event.target.value })}>
            <option value="">Select Player B</option>
            {optionsFor(match.playerBId).map((member) => (
              <option key={member.player_id} value={member.player_id}>
                {member.player?.name || member.player_id}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className="danger" onClick={onRemove}>
          Remove
        </button>
      </div>
    </div>
  );
}

export function MatchDayCard({
  leagueId,
  matchDay,
  courses,
  courseName,
  onUpdate,
  onDelete,
}: {
  leagueId: string;
  matchDay: {
    id: string;
    date: string;
    status: string;
    weekNumber?: number;
    hasScores?: boolean;
    course_id: string | null;
    hammer_hole?: number | null;
    is_playoff_week?: boolean;
  };
  courses: Array<{ id: string; name: string }>;
  courseName: string;
  onUpdate: (updates: { date?: string; courseId?: string | null; hammerHole?: string; isPlayoffWeek?: boolean }) => Promise<unknown>;
  onDelete: () => void;
}) {
  const [form, setForm] = useState({
    date: toDateInputValue(matchDay.date),
    courseId: matchDay.course_id,
    hammerHole: matchDay.hammer_hole ? String(matchDay.hammer_hole) : '',
    isPlayoffWeek: matchDay.is_playoff_week ?? false,
  });

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <strong>
          {matchDay.date} (Week {matchDay.weekNumber || 0})
        </strong>
        {badge(matchDay.status)}
        {matchDay.is_playoff_week ? <span className="badge">Playoff Week</span> : null}
      </div>
      <p className="muted">
        Has scores: {matchDay.hasScores ? 'Yes' : 'No'} | Course: {courseName} |{' '}
        {matchDay.hammer_hole ? `Hammer Hole ${matchDay.hammer_hole}` : 'No hammer hole'}
      </p>
      <div className="inline-actions">
        <Link className="nav-link" to={`/leagues/${leagueId}/admin/match-days/${matchDay.id}`}>
          Open Setup
        </Link>
        <Link className="nav-link" to={`/leagues/${leagueId}/admin/score-entry?matchDayId=${matchDay.id}`}>
          Score Entry
        </Link>
      </div>
      {matchDay.status === 'scheduled' ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void onUpdate({ date: form.date, courseId: form.courseId, hammerHole: form.hammerHole, isPlayoffWeek: form.isPlayoffWeek });
          }}
        >
          <div className="row">
            <div className="field">
              <label>Date</label>
              <input type="date" value={form.date} onChange={(event) => setForm((current) => ({ ...current, date: event.target.value }))} />
            </div>
            <div className="field">
              <label>Course</label>
              <select value={form.courseId ?? ''} onChange={(event) => setForm((current) => ({ ...current, courseId: event.target.value || null }))}>
                <option value="">TBD</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Hammer Hole</label>
              <select value={form.hammerHole} onChange={(event) => setForm((current) => ({ ...current, hammerHole: event.target.value }))}>
                <option value="">No hammer hole</option>
                {hammerOptions().map((hole) => (
                  <option key={hole} value={hole}>
                    Hole {hole}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <label className="absent-toggle">
              <input
                type="checkbox"
                checked={form.isPlayoffWeek}
                onChange={(event) => setForm((current) => ({ ...current, isPlayoffWeek: event.target.checked }))}
              />
              This is a playoff week
            </label>
          </div>
          <div className="inline-actions">
            <button type="submit">Save</button>
            <button className="danger" type="button" onClick={onDelete}>
              Delete
            </button>
          </div>
        </form>
      ) : matchDay.status === 'completed' ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void onUpdate({ courseId: form.courseId });
          }}
        >
          <p className="alert warn">
            Scores already exist. You can still correct the course until this match day is locked. Saved hole scores stay
            in place and the stored scoring details are recalculated against the selected course.
          </p>
          <div className="field">
            <label>Course</label>
            <select value={form.courseId ?? ''} onChange={(event) => setForm((current) => ({ ...current, courseId: event.target.value || null }))}>
              <option value="">Select course...</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
          <div className="inline-actions">
            <button type="submit" disabled={!form.courseId}>
              Update Course
            </button>
          </div>
        </form>
      ) : (
        <p className="muted">Locked rounds cannot be changed or deleted.</p>
      )}
    </div>
  );
}
